import { useState } from "react";
import { Layers, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const TIERS = ["Economy", "Best Value", "Premium"];

export function QuoteOptionsDialog({ it, onClose }) {
  const existing = it.data?.options || [];
  const [rows, setRows] = useState(TIERS.map((t) => {
    const e = existing.find((o) => o.tier === t) || {};
    return { tier: t, price: e.price ?? "", summary: e.summary ?? "", inclusions: e.inclusions ?? "" };
  }));
  const [busy, setBusy] = useState(false);
  const upd = (i, k, v) => setRows((r) => r.map((x, j) => j === i ? { ...x, [k]: v } : x));

  const save = async () => {
    setBusy(true);
    try {
      const options = rows.filter((r) => r.price !== "" || r.summary || r.inclusions)
        .map((r) => ({ tier: r.tier, price: r.price === "" ? null : Number(r.price), summary: r.summary, inclusions: r.inclusions }));
      await api.post(`/quotations/${it.record_id}/options`, { options });
      toast.success("Quote options saved — customers now see Economy / Best Value / Premium");
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="quote-options-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Layers className="h-4 w-4 text-purple-500" /> Quote Options · {it.title}</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">Offer up to three tiers in one branded quotation. Prices are customer-facing only — internal margins are never shown.</DialogDescription>
        </DialogHeader>
        <div className="mt-3 space-y-3">
          {rows.map((r, i) => (
            <div key={r.tier} className="rounded-xl border border-slate-200 p-3" data-testid={`quote-tier-${i}`}>
              <div className="mb-2 flex items-center justify-between">
                <span className="font-semibold text-slate-800">{r.tier}</span>
                <div className="flex items-center gap-1 text-sm"><span className="text-slate-400">₹</span>
                  <Input type="number" min="0" value={r.price} onChange={(e) => upd(i, "price", e.target.value)} placeholder="Price" className="h-8 w-32" data-testid={`quote-tier-price-${i}`} /></div>
              </div>
              <Input value={r.summary} onChange={(e) => upd(i, "summary", e.target.value)} placeholder="One-line summary (e.g. 3★ stay, group transfers)" className="h-8 mb-2" data-testid={`quote-tier-summary-${i}`} />
              <textarea value={r.inclusions} onChange={(e) => upd(i, "inclusions", e.target.value)} placeholder="Inclusions (one per line)" rows={3}
                className="w-full rounded-lg border border-slate-200 p-2 text-sm outline-none focus:border-blue-400" data-testid={`quote-tier-inclusions-${i}`} />
            </div>
          ))}
        </div>
        <button onClick={save} disabled={busy} data-testid="quote-options-save" className="mt-3 inline-flex items-center gap-2 rounded-full brand-gradient-bg px-5 py-2 text-sm font-semibold text-white disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Layers className="h-4 w-4" />} Save options
        </button>
      </DialogContent>
    </Dialog>
  );
}
